/**
 * REGLAS DEL COTIZADOR UNIFICADO — funciones puras, sin Angular ni HTTP.
 *
 * Todo lo que acá vive está auditado contra las pantallas vigentes; cada
 * constante cita su origen para que la divergencia se detecte al leer. Este
 * archivo es la única fuente de verdad de límites, matriz de destinos y
 * comisión: ningún componente debe redefinir estos números.
 *
 * Ver `docs/calcualdoraUnica/plan-de-trabajo-auditado.html`, pestaña 2.
 */

import {
  CotizacionVM,
  DestinoMeta,
  DestinoRemesa,
  OrigenRemesa,
  PerfilUsuario,
} from '../models/remittance.model';

// ───────────────────────── Límites ─────────────────────────
// Cinco mínimos distintos conviven en el sistema. No unificarlos "para
// simplificar": cada uno lo impone un riel diferente.

/** Riel propio CLP↔VES. Mínimo comercial de la remesa CLP→VES. */
export const FIAT_MIN_CLP = 15_000;
export const FIAT_MAX_CLP = 5_000_000;

/** Riel USDT→VES. Espejo de `ConvertUsdtVesComponent.MIN_USDT_AMOUNT` (:46). */
export const MIN_USDT_VES = 10;

/**
 * Riel USDT→CLP. Espejo de `ConvertUsdtClpComponent.MIN_USDT_AMOUNT` (:23).
 * OJO: ese flujo exige además **saldo ≥ mínimo**, no sólo monto ≥ mínimo.
 */
export const MIN_USDT_CLP = 20;

/** Rieles Vita (COP/PEN). Espejo de `VitaCalculatorDemoComponent:61-62`. */
export const VITA_MIN_USD = 20;
export const VITA_MAX_USD = 2_500;

/**
 * Mínimo de los rieles Vita en moneda de origen fiat, tal como lo tienen las
 * pantallas vigentes: `montoClp = 20000` y `montoVes = 20000`, las dos
 * documentadas como «~20 USD equivalente»
 * (`vita-calculator-demo.component.ts:264, :282`).
 *
 * El backend igual manda `minClp` / `minVes` con cada cotización, y ésos son
 * los que bloquean el envío. Estas constantes existen para NO tener que
 * preguntar antes de poder mostrar algo: con ellas se elige el monto inicial
 * de una vez y se cotiza una sola vez.
 */
export const VITA_MIN_CLP = 20_000;
export const VITA_MIN_VES = 20_000;

/**
 * Monto VES de referencia para pedir la tasa VES→CLP sin conocerla todavía.
 * Equivale a ~10.000 CLP; mismo valor que `obtenerTasaCambioInicial`.
 *
 * Existe porque `rates-public/ves-clp/por-monto` responde **500** (no 4xx) si
 * el monto cae fuera de los tramos. Hay que entrar con un valor seguro y
 * recién después derivar el mínimo real en VES.
 *
 * Desde el 03/11/2026 los límites en VES salen del mid (`limitesVesDesdeMid`)
 * y este monto queda para el sondeo y para el RESPALDO sin `clpPorVesMid`.
 */
export const VES_MONTO_REFERENCIA = 33_000;

/**
 * Motivo de VES→CLP cuando el monto está dentro de los límites pero la
 * cotización exacta falló. Antes se caía en silencio a la tasa de referencia
 * —la de la franja más cara— y el cliente recibía hasta ~2,9 % menos sin
 * enterarse: ahora no se cotiza con otra tasa que la del monto.
 */
export const MOTIVO_SIN_COTIZACION_EXACTA =
  'No pudimos calcular el envío en este momento. Intenta de nuevo en unos segundos.';

/** Techo por defecto de Pago Móvil si el parámetro no está configurado. */
export const PAGO_MOVIL_MONTO_MAX_FALLBACK = 100_000;

// ───────────────────────── Destinos ─────────────────────────

export const DESTINOS: Record<DestinoRemesa, DestinoMeta> = {
  VE: {
    code: 'VE',
    nombre: 'Venezuela',
    moneda: 'VES',
    flag: 'assets/flags/venezuela.png',
    libreta: 'VENEZUELA',
  },
  CL: {
    code: 'CL',
    nombre: 'Chile',
    moneda: 'CLP',
    flag: 'assets/flags/chile.png',
    libreta: 'CHILE',
  },
  CO: {
    code: 'CO',
    nombre: 'Colombia',
    moneda: 'COP',
    flag: 'assets/flags/colombia.png',
    libreta: 'VITA',
  },
  PE: {
    code: 'PE',
    nombre: 'Perú',
    moneda: 'PEN',
    flag: 'assets/flags/peru.png',
    libreta: 'VITA',
  },
};

/**
 * Matriz de destinos por (perfil, origen). Corrige dos supuestos erróneos:
 *
 *  1. La grilla de países NO es fija: para un usuario VE el destino natural
 *     es CHILE, no Venezuela.
 *  2. `USDT→CLP` de un usuario CL **no es una remesa** — es una conversión a
 *     su propia wallet, sin beneficiario. Por eso no figura acá.
 *     (`ConvertUsdtClpComponent` bifurca por `tipoId`.)
 *
 * `CL.VES` es el chileno con saldo VES (ver `origenesDe`): sus bolívares solo
 * salen a Chile (VES→CLP, `retiroVesClp`). Las remesas VES→COP/PEN (Vita) no
 * están habilitadas para él.
 */
const MATRIZ: Record<PerfilUsuario, Record<OrigenRemesa, DestinoRemesa[]>> = {
  CL: {
    CLP: ['VE', 'CO', 'PE'],
    USDT: ['VE', 'CO', 'PE'],
    VES: ['CL'],
  },
  VE: {
    VES: ['CL', 'CO', 'PE'],
    USDT: ['CL', 'CO', 'PE'],
    CLP: [],
  },
};

/**
 * Perfil de remesas: `VE` solo para `tipoId === 'VE'` exacto. CCVE, V o un
 * tipo vacío con cédula V/E tienen saldo VES (`shared/utils/saldo-ves`), pero
 * NO son perfil VE acá: el back gatea las remesas VES→COP/PEN
 * (`vita-ves-cop/pen-remesa`) y el USDT→CLP del venezolano (`retiroUsdtClpVes`)
 * con `tipoId === 'VE'`. Por eso operan como el chileno con saldo VES
 * (`origenesDe('CL', true)`: VES solo hacia Chile).
 */
export function perfilDeTipoId(tipoId: string | null | undefined): PerfilUsuario {
  return tipoId === 'VE' ? 'VE' : 'CL';
}

/**
 * Monedas con las que puede pagar el perfil. La local primero. El chileno con
 * saldo VES (`tieneSaldoVes`) suma bolívares al final: lo CLP no cambia.
 */
export function origenesDe(perfil: PerfilUsuario, conSaldoVes = false): OrigenRemesa[] {
  if (perfil === 'VE') return ['VES', 'USDT'];
  return conSaldoVes ? ['CLP', 'USDT', 'VES'] : ['CLP', 'USDT'];
}

/**
 * ¿El aviso de falta de saldo ofrece «Recargar» la moneda con la que paga?
 * CLP y USDT, siempre. VES, solo el perfil VE (`tipoId === 'VE'`, el mismo
 * criterio que `puedeRecargarVes` de `shared/utils/saldo-ves`).
 * 30/10/2026: el chileno no recarga bolívares. Pagando con VES (solo hacia
 * Chile) no tiene botón: comprar VES con CLP para volver a CLP no sirve.
 */
export function ofreceRecarga(perfil: PerfilUsuario, origen: OrigenRemesa): boolean {
  return origen !== 'VES' || perfil === 'VE';
}

/** Destinos válidos para (perfil, origen). Reactivo, nunca fijo. */
export function destinosDe(
  perfil: PerfilUsuario,
  origen: OrigenRemesa,
): DestinoMeta[] {
  return (MATRIZ[perfil][origen] || []).map((c) => DESTINOS[c]);
}

/** ¿El par es una combinación de remesa válida? */
export function esParValido(
  perfil: PerfilUsuario,
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): boolean {
  return (MATRIZ[perfil][origen] || []).includes(destino);
}

/** El destino usa el riel propio de Aguacate (no Vita). */
export function esRielPropio(destino: DestinoRemesa): boolean {
  return destino === 'VE' || destino === 'CL';
}

/**
 * El riel del destino tiene horario de atención (`FIAT`/`FIAT_STOP`).
 * Los rieles Vita no lo tienen.
 */
export function tieneHorarioFiat(destino: DestinoRemesa): boolean {
  return esRielPropio(destino);
}

// ───────────────────────── Comisión ─────────────────────────

/**
 * SUBTOTAL en moneda de DESTINO: lo que da la tasa mostrada, antes de la
 * comisión. Es la primera mitad de la identidad que el cliente puede rehacer
 * con lo único que tiene delante:
 *
 *     subtotal − comisión = recibe
 *
 * Se calcula con la tasa REDONDEADA a los decimales que se imprimen, no con la
 * exacta. Ahí está la clave de que cierre: dividiendo por la exacta y
 * mostrando la redondeada, la cuenta de la pantalla y la del cliente usan
 * varas distintas y la resta falla — 8 COP en CLP→COP, 12 en USDT→COP y hasta
 * 820 en el tope del riel.
 */
export function subtotalEnDestino(
  monto: number,
  tasa: number | null | undefined,
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): number | null {
  if (!tasa || !monto) return null;
  return redondearDestino(destino, monto * redondearTasa(tasa, origen, destino));
}

/**
 * COMISIÓN, en moneda de DESTINO. Reemplaza tres implementaciones idénticas de
 * `VitaCalculatorDemoComponent` (`comisionClp:572`, `comisionVes:904`,
 * `displayComisionUsd:1371`), que la informaban en moneda de origen.
 *
 * Va en destino porque es la ÚNICA forma de que la cuenta cierre con números
 * enteros. En origen, cada peso de comisión mueve el resultado 3,28 COP, así
 * que la resta sólo puede caer CERCA del recibe, nunca encima; con USDT, donde
 * un centavo vale 31 COP, el error llegaba a 15. En destino es exacta por
 * construcción.
 *
 * Incluye el fee fijo y el spread, igual que antes, y sigue siendo un valor
 * DERIVADO de lo que ya manda el backend: no recalcula el envío ni viaja en él
 * (el POST manda `expectedRecibe*`, nunca la comisión).
 *
 * Devuelve `null` cuando no hay nada honesto que mostrar: sin tasa, o cuando
 * el subtotal no supera al recibe. Ese segundo caso cubre el riel que no
 * publica referencia —la tasa mostrada es entonces la efectiva y no hay
 * comisión que separar— y la referencia degenerada por debajo de la aplicada,
 * que el backend ya evita devolviendo `null`.
 */
export function comisionEnDestino(
  monto: number,
  recibe: number | null | undefined,
  tasa: number | null | undefined,
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): number | null {
  if (!recibe) return null;
  const subtotal = subtotalEnDestino(monto, tasa, origen, destino);
  if (subtotal == null) return null;
  const comision = redondearDestino(destino, subtotal - recibe);
  return comision > 0 ? comision : null;
}

// ───────────────────────── Límites por par ─────────────────────────

export interface LimitesPar {
  min: number | null;
  max: number | null;
}

/**
 * Límites del par, en moneda de ORIGEN.
 *
 * Con origen CLP o VES hacia Vita, el backend YA devuelve los límites
 * convertidos (`minClp`/`maxClp`, `minVes`/`maxVes`) — se usan ésos.
 * Con origen USDT los contratos `CotizacionCo` / `CotizacionPe` **no los
 * traen**, y hay que aplicar los del front.
 */
/**
 * Límites CLP del riel propio publicados por el servidor (`limites` de
 * `rates-public/ultimas-agrupadas` y de su par VES→CLP), ya validados.
 *
 * `null` si no vienen o vienen raros (no numéricos, ≤ 0, máximo bajo el
 * mínimo): en ese caso mandan `FIAT_MIN_CLP` / `FIAT_MAX_CLP`, exactamente
 * como antes de las franjas dinámicas.
 */
export function limitesClpDesdeRespuesta(data: any): LimitesPar | null {
  const lim = data?.limites;
  if (!lim || typeof lim !== 'object') return null;
  const min = Number(lim.min);
  const max = Number(lim.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (!(min > 0) || !(max > min)) return null;
  return { min, max };
}

/** Límites CLP del riel propio: los del servidor si son válidos, si no los fijos. */
export function limitesClpRielPropio(
  delServidor?: LimitesPar | null,
): { min: number; max: number } {
  const min = delServidor?.min;
  const max = delServidor?.max;
  if (min != null && max != null && min > 0 && max > min) {
    return { min, max };
  }
  return { min: FIAT_MIN_CLP, max: FIAT_MAX_CLP };
}

export function limitesDe(
  origen: OrigenRemesa,
  destino: DestinoRemesa,
  delBackend?: LimitesPar,
  clpServidor?: LimitesPar | null,
): LimitesPar {
  if (origen === 'USDT') {
    if (destino === 'VE') return { min: MIN_USDT_VES, max: null };
    if (destino === 'CL') return { min: MIN_USDT_CLP, max: null };
    return { min: VITA_MIN_USD, max: VITA_MAX_USD };
  }

  if (esRielPropio(destino)) {
    // CLP→VES usa los límites tal cual; VES→CL los deriva con el mid
    // (ver `limitesVesDesdeMid`), así que acá sólo aplica el caso CLP.
    // Con franjas dinámicas el servidor publica el mínimo y el máximo; sin
    // ellos, las constantes de siempre.
    return limitesClpRielPropio(clpServidor);
  }

  return {
    min: delBackend?.min ?? null,
    max: delBackend?.max ?? null,
  };
}

/**
 * Límites que el front YA conoce, sin preguntarle nada al backend.
 *
 * Nueve de los diez pares tienen mínimo constante —los mismos que usan las
 * pantallas vigentes—, así que al elegir destino se puede fijar el monto y
 * cotizar UNA sola vez. El único que devuelve `null` es `VES→CL`: su mínimo
 * está en VES y se deriva del mid (ver `limitesVesDesdeMid`).
 *
 * El máximo de los rieles Vita con origen fiat queda en `null` a propósito: lo
 * publica la cotización (`maxClp`/`maxVes`) y no hay constante que lo espeje.
 */
export function limitesLocales(
  origen: OrigenRemesa,
  destino: DestinoRemesa,
  clpServidor?: LimitesPar | null,
): LimitesPar | null {
  if (origen === 'USDT') return limitesDe(origen, destino);

  if (esRielPropio(destino)) {
    // CLP→VE son constantes (o los del servidor, si ya se conocen); VES→CL
    // depende del mid.
    return origen === 'CLP'
      ? limitesDe(origen, destino, undefined, clpServidor)
      : null;
  }

  return { min: origen === 'CLP' ? VITA_MIN_CLP : VITA_MIN_VES, max: null };
}

/**
 * Monto inicial de `VES→CL`, el único par cuyo mínimo no se conoce sin
 * preguntar. Es 1,5 veces el monto de referencia —el mismo aire que deja
 * `semillaDesdeMinimo()` sobre el mínimo— y queda por encima del mínimo real
 * para cualquier tasa de hasta 5 VES por CLP. Si aun así quedara por debajo, la
 * cotización lo dice (`bajoMinimo`) y el monto se corrige con el mínimo real
 * que vino en ella, sin haber gastado una consulta de más.
 */
export const MONTO_INICIAL_VES_CL = 50_000;

/**
 * Límites del riel VES→CLP, derivados de la tasa vigente.
 * `tasaVesPorClp` es cuántos VES equivalen a 1 CLP.
 *
 * RESPALDO desde el 03/11/2026: sólo cuando el servidor no publica
 * `clpPorVesMid`. Con la tasa de referencia (la franja más cara) el mínimo
 * queda alto y el máximo también, porque el servidor no elige la franja con
 * la tasa del cliente sino con el mid (ver `limitesVesDesdeMid`).
 */
export function limitesVesDesdeTasa(
  tasaVesPorClp: number,
  clpServidor?: LimitesPar | null,
): LimitesPar {
  if (!(tasaVesPorClp > 0)) return { min: null, max: null };
  // `limites` del par VES→CLP viene en CLP: se convierte igual que las constantes.
  const clp = limitesClpRielPropio(clpServidor);
  return {
    min: Math.ceil(clp.min * tasaVesPorClp),
    max: Math.floor(clp.max * tasaVesPorClp),
  };
}

/**
 * `clpPorVesMid` de `rates-public/ves-clp/ultimas-agrupadas`: CLP por 1 VES al
 * mid, el número con que el servidor elige la franja VES→CLP
 * (`clpAlMid = round(VES × clpPorVesMid)`, `RatesService.clpAlMidVesClp`).
 * `null` si no viene (backend viejo) o no es un número positivo.
 */
export function clpPorVesMidDesdeRespuesta(data: any): number | null {
  const v = Number(data?.clpPorVesMid);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** Tope de vueltas de los ajustes finos: es ruido de coma flotante, alcanzan un par. */
const VUELTAS_AJUSTE = 100;

/**
 * El MENOR monto entero en VES que el servidor ubica en `clp` o más:
 * `round(VES × clpPorVesMid) ≥ clp`, con el mismo `Math.round` y la misma
 * multiplicación en coma flotante que `RatesService.clpAlMidVesClp`. Arranca
 * de la cuenta exacta (`round(x) ≥ clp ⟺ x ≥ clp − 0,5`) y corrige de a 1 VES
 * el ruido de la división. `null` sin mid válido.
 */
export function vesMinParaClp(clp: number, clpPorVesMid: number): number | null {
  if (!(clpPorVesMid > 0) || !Number.isFinite(clp)) return null;
  const alMid = (v: number) => Math.round(v * clpPorVesMid);
  let v = Math.max(0, Math.ceil((clp - 0.5) / clpPorVesMid));
  for (let k = 0; k < VUELTAS_AJUSTE && v > 0 && alMid(v - 1) >= clp; k++) v--;
  for (let k = 0; k < VUELTAS_AJUSTE && alMid(v) < clp; k++) v++;
  return v;
}

/**
 * El MAYOR monto entero en VES con `round(VES × clpPorVesMid) ≤ clp`. Espejo
 * de `vesMinParaClp` (`round(x) ≤ clp ⟺ x < clp + 0,5`). `null` sin mid válido.
 */
export function vesMaxParaClp(clp: number, clpPorVesMid: number): number | null {
  if (!(clpPorVesMid > 0) || !Number.isFinite(clp)) return null;
  const alMid = (v: number) => Math.round(v * clpPorVesMid);
  let v = Math.max(0, Math.floor((clp + 0.5) / clpPorVesMid));
  for (let k = 0; k < VUELTAS_AJUSTE && v > 0 && alMid(v) > clp; k++) v--;
  for (let k = 0; k < VUELTAS_AJUSTE && alMid(v + 1) <= clp; k++) v++;
  return v;
}

/**
 * Límites del riel VES→CLP en VES, con el MISMO criterio con que el servidor
 * elige la franja: los CLP al mid, no la tasa del cliente.
 *
 *  · mínimo = el menor VES con `round(VES × mid) ≥ mínimo CLP`;
 *  · máximo = el mayor VES con `round(VES × mid) ≤ máximo CLP`.
 *
 * Con la tasa de referencia (`limitesVesDesdeTasa`) el front aceptaba montos
 * sobre el máximo que `por-monto` rechaza (500) y bloqueaba en el piso ~550
 * VES que el servidor sí acepta. Sin mid válido, `{ null, null }`.
 */
export function limitesVesDesdeMid(
  clpPorVesMid: number | null | undefined,
  clpServidor?: LimitesPar | null,
): LimitesPar {
  if (clpPorVesMid == null || !(clpPorVesMid > 0)) return { min: null, max: null };
  // `limites` del par VES→CLP viene en CLP (o mandan las constantes).
  const clp = limitesClpRielPropio(clpServidor);
  return {
    min: vesMinParaClp(clp.min, clpPorVesMid),
    max: vesMaxParaClp(clp.max, clpPorVesMid),
  };
}

// ───────────────────────── Franjas del riel propio ─────────────────────────

/**
 * Una franja (tramo por monto) del riel propio tal como la publica
 * `ultimas-agrupadas` en `data.tramos`: clave y cortes. En los dos pares los
 * cortes vienen en CLP; `franjasEnOrigen` los pasa a la moneda que se paga.
 */
export interface FranjaPublica {
  clave: string;
  desde: number;
  hasta: number;
}

/**
 * `data.tramos` de `ultimas-agrupadas`, validado y ordenado por `desde`. `null`
 * si no viene (backend viejo) o no queda ninguna franja legible.
 */
export function franjasDesdeRespuesta(data: any): FranjaPublica[] | null {
  const lista: any[] = Array.isArray(data?.tramos) ? data.tramos : [];
  const franjas = lista
    .map((t) => ({
      clave: t?.clave != null ? String(t.clave) : '',
      desde: Number(t?.desde),
      hasta: Number(t?.hasta),
    }))
    .filter(
      (f) =>
        !!f.clave &&
        Number.isFinite(f.desde) &&
        Number.isFinite(f.hasta) &&
        f.desde <= f.hasta,
    )
    .sort((a, b) => a.desde - b.desde);
  return franjas.length ? franjas : null;
}

/**
 * Las franjas con los cortes en la moneda de ORIGEN, en enteros: tal cual en
 * CLP→VES; en VES→CLP, convertidos con el mid como los convierte el servidor
 * (`vesMinParaClp` / `vesMaxParaClp`). `null` si no hay franjas o, en VES,
 * falta el mid.
 */
export function franjasEnOrigen(
  franjas: FranjaPublica[] | null | undefined,
  monedaOrigen: 'CLP' | 'VES',
  clpPorVesMid?: number | null,
): FranjaPublica[] | null {
  if (!franjas || !franjas.length) return null;
  if (monedaOrigen === 'CLP') return franjas;
  const mid = Number(clpPorVesMid);
  if (!(mid > 0)) return null;
  const enVes: FranjaPublica[] = [];
  for (const f of franjas) {
    const desde = vesMinParaClp(f.desde, mid);
    const hasta = vesMaxParaClp(f.hasta, mid);
    if (desde != null && hasta != null && desde <= hasta) {
      enVes.push({ clave: f.clave, desde, hasta });
    }
  }
  return enVes.length ? enVes : null;
}

/**
 * Monto con el que se SONDEA el par para descubrir su mínimo real.
 *
 * No es el monto que se le ofrece al usuario: es un valor seguro, dentro de
 * rango, con el que se puede pedir la primera cotización cuando todavía no se
 * conoce ni la tasa ni el mínimo. Con la cotización en mano, el monto que se
 * muestra lo decide `semillaDesdeMinimo()`.
 *
 * Es el mismo orden que usa la pantalla vigente
 * (`FiatCalculatorComponent.obtenerTasaCambioInicial:1331`): sondear, derivar
 * el mínimo con la tasa, y recién entonces fijar el monto.
 */
export function montoSondeo(
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): number {
  if (origen === 'USDT') return limitesDe(origen, destino).min ?? VITA_MIN_USD;
  if (origen === 'VES') return VES_MONTO_REFERENCIA;
  return 50_000;
}

/** Paso de redondeo legible por moneda de origen. */
function pasoDe(origen: OrigenRemesa): number {
  if (origen === 'USDT') return 5;
  if (origen === 'CLP') return 5_000;
  return 1_000; // VES
}

/** Factor sobre el mínimo. Deja aire para bajar el monto sin caer en el borde. */
const FACTOR_SEMILLA = 1.5;

/**
 * Monto inicial del par, DERIVADO del mínimo real que devolvió la cotización.
 *
 * Arranca por encima del mínimo, nunca en él: el mínimo es el borde exacto
 * donde el envío queda bloqueado, y abrir ahí deja al usuario mirando un error
 * que no provocó. Era el bug del arranque — la semilla era una constante
 * (33.000 VES, 20 USD) que el mínimo real superaba en cuanto se movía la tasa.
 *
 * Si el saldo alcanza para el mínimo pero no para la semilla, se usa el saldo:
 * es preferible abrir con un monto pagable que con uno que ya avisa
 * "tu saldo no alcanza".
 */
export function semillaDesdeMinimo(
  origen: OrigenRemesa,
  min: number | null,
  max: number | null,
  saldo: number | null = null,
): number | null {
  if (min == null || !(min > 0)) return null;

  const paso = pasoDe(origen);
  let semilla = Math.ceil((min * FACTOR_SEMILLA) / paso) * paso;

  if (max != null && semilla > max) semilla = max;

  // El saldo sólo recorta si igual queda por encima del mínimo.
  if (saldo != null && saldo >= min && semilla > saldo) {
    const recortado = Math.floor(saldo / paso) * paso;
    semilla = Math.max(min, recortado);
  }

  return semilla;
}

/**
 * Atajos de monto derivados de la semilla. Ninguno cae por debajo del mínimo
 * ni por encima del máximo, y no se repiten.
 */
export function chipsDesdeSemilla(
  origen: OrigenRemesa,
  semilla: number | null,
  max: number | null,
): number[] {
  if (semilla == null || !(semilla > 0)) return [];
  const paso = pasoDe(origen);
  const crudos = [semilla, semilla * 2, semilla * 5].map(
    (v) => Math.round(v / paso) * paso,
  );
  const dentro = crudos.filter((v) => v > 0 && (max == null || v <= max));
  return Array.from(new Set(dentro));
}

// ───────────────────── Cálculo en las dos direcciones ─────────────────────

/** ¿El usuario está tipeando el monto que paga o el que recibe el beneficiario? */
export type DireccionCalculo = 'ORIGEN' | 'DESTINO';

/**
 * Monto de origen que produce `recibe`, con la tasa que ya se conoce.
 *
 * En los rieles propios es exacto: la tasa es un multiplicador limpio que el
 * front tiene. En los rieles Vita es sólo la primera estimación —la cotización
 * es opaca y no hay endpoint inverso—, y la corrige `siguienteEstimacion()`.
 *
 * `tasaEfectiva` está SIEMPRE normalizada a unidades de destino por 1 de
 * origen —`RemittanceQuoteService` ya invierte el caso VES→CLP, donde el
 * backend publica VES por 1 CLP—, así que el inverso siempre divide.
 */
export function invertirLocal(
  recibe: number | null | undefined,
  tasaEfectiva: number | null | undefined,
): number | null {
  if (!recibe || !tasaEfectiva || tasaEfectiva <= 0) return null;
  return recibe / tasaEfectiva;
}

/**
 * Siguiente estimación del monto de origen para acercarse al objetivo — un
 * paso de secante sobre la tasa MARGINAL.
 *
 * Con los rieles Vita vale `(monto − comisión) × referencia = recibe`, y la
 * comisión no escala con el monto: la derivada del recibe respecto del monto es
 * la tasa de referencia, no la efectiva. Por eso un solo paso alcanza.
 * Cuando el riel no publica referencia se usa la efectiva, que en los rieles
 * propios es exacta.
 */
export function siguienteEstimacion(
  objetivo: number,
  monto: number,
  recibe: number | null | undefined,
  tasaMarginal: number | null | undefined,
): number | null {
  if (recibe == null || !tasaMarginal || tasaMarginal <= 0) return null;
  const siguiente = monto + (objetivo - recibe) / tasaMarginal;
  return siguiente > 0 ? siguiente : null;
}

/** Error relativo que se acepta sin volver a cotizar. */
export const TOLERANCIA_OBJETIVO = 0.002; // 0,2 %

/**
 * ¿Vale la pena otra cotización para acercarse al objetivo? Se compara en
 * relativo porque redondear el monto de origen a una unidad pagable ya mueve
 * el recibe: exigir exactitud sería pedir infinitas llamadas.
 */
export function necesitaRefinar(
  objetivo: number,
  recibe: number | null | undefined,
): boolean {
  if (recibe == null) return false;
  if (!(objetivo > 0)) return false;
  return Math.abs(recibe - objetivo) / objetivo > TOLERANCIA_OBJETIVO;
}

/**
 * Monto de origen redondeado a una unidad que el usuario pueda pagar de
 * verdad: las fiat son enteras, USDT lleva centavos.
 */
export function redondearOrigen(origen: OrigenRemesa, monto: number): number {
  return origen === 'USDT' ? Math.round(monto * 100) / 100 : Math.round(monto);
}

/** Monto de destino redondeado a la unidad de su moneda (PEN lleva centavos). */
export function redondearDestino(destino: DestinoRemesa, monto: number): number {
  return destino === 'PE' ? Math.round(monto * 100) / 100 : Math.round(monto);
}

/** `Math.ceil` sin el ruido de coma flotante: 10.313,0000000002 no sube a 10.314. */
function techoSinRuido(v: number): number {
  return Math.ceil(Math.round(v * 1e6) / 1e6);
}

// ─────────────── Cálculo inverso que cruza un corte de franja ───────────────
//
// En los rieles propios la tasa sube de golpe al pasar de franja (con el motor
// de márgenes, ~1,45 % entre vecinas y configurable hasta 20 %). Un objetivo
// que cae en el HUECO entre dos franjas —más de lo que da el techo de la
// chica, menos de lo que da el piso de la grande— no tiene monto exacto, y la
// secante rebota de un lado al otro del corte. Antes se quedaba con la más
// cercana, que solía ser la de abajo: con objetivo 100.000 VES pagaba 99.980
// CLP y recibía 98.570, cuando 100.000 CLP daban 100.020.

/**
 * ¿El recibe ya cubre el objetivo, tal como se ve en pantalla (redondeado a la
 * unidad de la moneda de destino)?
 */
export function alcanzaObjetivo(
  c: Pick<CotizacionVM, 'disponible' | 'recibe' | 'destino'> | null | undefined,
  objetivo: number,
): boolean {
  return (
    !!c &&
    c.disponible &&
    c.recibe != null &&
    redondearDestino(c.destino, c.recibe) >= objetivo
  );
}

/**
 * Las dos estimaciones del cálculo inverso quedaron a los dos lados de un
 * corte: la de menor monto en una franja y sin llegar al objetivo, la de mayor
 * monto en OTRA franja y cubriéndolo. Es la señal de que el objetivo cae cerca
 * del corte —o en el hueco— y la secante no lo va a encontrar sola.
 *
 * `null` si no es el caso: misma franja, alguna sin franja (rieles Vita, USDT)
 * o las dos del mismo lado del objetivo. Ahí manda la regla de siempre.
 */
export function cruceDeFranja<T extends CotizacionVM>(
  objetivo: number,
  a: T,
  b: T,
): { bajo: T; alto: T } | null {
  const conFranja = (c: T) => c.disponible && c.recibe != null && c.franja != null;
  if (!conFranja(a) || !conFranja(b) || a.franja === b.franja) return null;
  const [bajo, alto] = a.monto <= b.monto ? [a, b] : [b, a];
  if (alcanzaObjetivo(bajo, objetivo) || !alcanzaObjetivo(alto, objetivo)) return null;
  return { bajo, alto };
}

/**
 * El monto que falta cotizar cuando el cálculo inverso cruzó un corte, con los
 * cortes de las franjas en moneda de ORIGEN (`franjasEnOrigen`):
 *
 *  1. si el objetivo entra en la franja del `bajo` —con SU tasa, que es la
 *     exacta de esa franja—, el menor monto entero que lo cubre ahí adentro;
 *  2. si no entra, el objetivo cae en el hueco: el piso (`desde`) de la franja
 *     siguiente, el menor monto que lo supera.
 *
 * El 1 evita pagar de más cuando el objetivo sí cabía en la franja de abajo
 * (pasa cuando la estimación arrancó de una franja lejana). `null` si falta
 * algún dato o la franja del `bajo` no está en la lista.
 */
export function montoCruceFranja(
  objetivo: number,
  bajo: Pick<CotizacionVM, 'monto' | 'tasaEfectiva' | 'franja'>,
  franjas: FranjaPublica[] | null | undefined,
): number | null {
  const tasa = bajo.tasaEfectiva;
  if (!franjas || !franjas.length || bajo.franja == null) return null;
  if (tasa == null || !(tasa > 0) || !(objetivo > 0)) return null;

  const orden = [...franjas].sort((x, y) => x.desde - y.desde);
  const i = orden.findIndex((f) => f.clave === bajo.franja);
  if (i < 0) return null;

  const propia = orden[i];
  const enPropia = Math.max(propia.desde, techoSinRuido(objetivo / tasa));
  if (enPropia <= propia.hasta && enPropia > bajo.monto) return enPropia;

  const siguiente = orden[i + 1];
  return siguiente && siguiente.desde > bajo.monto ? siguiente.desde : null;
}

/**
 * De las candidatas, la de MENOR monto de origen cuyo recibe cubre el
 * objetivo. Es la regla cuando se cruza un corte: nunca menos de lo pedido, y
 * lo menos posible de más. `null` si ninguna lo cubre.
 */
export function menorQueAlcanza<T extends CotizacionVM>(
  objetivo: number,
  candidatas: Array<T | null | undefined>,
): T | null {
  let mejor: T | null = null;
  for (const c of candidatas) {
    if (!c || !alcanzaObjetivo(c, objetivo)) continue;
    if (!mejor || c.monto < mejor.monto) mejor = c;
  }
  return mejor;
}

/*
 * Acá vivía `limitesEnDestino()`: convertía el mínimo y el máximo del par a
 * moneda de destino para el texto "Entre X y Y COP aprox.". Se eliminó porque
 * con un solo dato no se puede calcular bien. Multiplicaba por la tasa
 * EFECTIVA del monto tipeado, que ya trae adentro el fee fijo del riel, y al
 * estirarla hasta el tope el error llegaba a 229.000 COP; con la de
 * referencia habría errado para el otro lado, prometiendo de más. El bloqueo
 * real siempre salió de `bajoMinimo`/`excedeMaximo`, en moneda de origen, y
 * los límites en origen se siguen mostrando: son los que valida el backend.
 */

// ───────────────────────── Formato y plazos ─────────────────────────

/**
 * La ÚNICA tasa que se le muestra al cliente, en cualquier pantalla.
 *
 * Cuando el riel publica referencia se muestra ÉSA y sólo ésa: poner además la
 * aplicada deja dos tasas distintas para el mismo envío y el cliente termina
 * restando el spread. La diferencia ya está informada como comisión, en moneda
 * de origen, y la identidad cierra: `(monto − comisión) × referencia = recibe`.
 *
 * Vive acá —y no en un getter del componente— porque el resumen, el desglose y
 * el comprobante tienen que decir el MISMO número: si cada uno elige, un
 * comprobante puede contradecir la pantalla que lo generó.
 */
export function tasaMostrada(c: CotizacionVM | null | undefined): number | null {
  return c?.tasaReferencia ?? c?.tasaEfectiva ?? null;
}

/** Nombra cuál de las dos tasas se está mostrando. */
export function etiquetaTasaMostrada(c: CotizacionVM | null | undefined): string {
  return c?.tasaReferencia != null ? 'Tasa de referencia' : 'Tasa aplicada';
}

/** PEN se muestra con 2 decimales; COP, VES y CLP son enteros. */
export function decimalesDe(destino: DestinoRemesa): string {
  return destino === 'PE' ? '1.2-2' : '1.0-0';
}

/**
 * Decimales con los que se IMPRIME la tasa de cada par. Espejo exacto de los
 * formatos de las pantallas vigentes: `fmtRefClp`/`fmtRefVes` (:938, :899) y
 * el `1.2-2` del encabezado del bloque USD
 * (`vita-calculator-demo.component.html:234`).
 *
 * PEN por CLP o por VES vive en milésimas (0,003647): con dos decimales se
 * mostraría "0,00". Los rieles propios conservan el formato amplio del
 * cotizador — no publican referencia y su tasa es la única que se ve.
 *
 * Es FUENTE ÚNICA a propósito: de acá salen tanto el formato del pipe como el
 * redondeo con el que se calculan el subtotal y la comisión. Si cada uno
 * eligiera por su cuenta, la pantalla mostraría una tasa y la cuenta usaría
 * otra — que es exactamente lo que hacía que el desglose no cerrara.
 */
export function decimalesTasa(
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): number {
  if (esRielPropio(destino)) return 6;
  if (destino === 'PE' && origen !== 'USDT') return 6;
  if (destino === 'CO' && origen === 'VES') return 4;
  return 2;
}

/** La tasa tal cual queda impresa: redondeada a los decimales que se muestran. */
export function redondearTasa(
  tasa: number,
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): number {
  const f = Math.pow(10, decimalesTasa(origen, destino));
  return Math.round(tasa * f) / f;
}

/** Máscara del pipe para la tasa del par. Los mínimos vienen de las pantallas vigentes. */
export function formatoTasa(origen: OrigenRemesa, destino: DestinoRemesa): string {
  const min = destino === 'PE' && origen !== 'USDT' ? 4 : 2;
  return `1.${min}-${decimalesTasa(origen, destino)}`;
}

/**
 * Formato de la comisión. Va en moneda de DESTINO (ver `comisionEnDestino`),
 * así que la máscara es la del destino: entera en COP, VES y CLP, con
 * centavos en PEN. Un decimal en pesos o en bolívares no informa nada.
 */
export function formatoComision(destino: DestinoRemesa): string {
  return decimalesDe(destino);
}

/**
 * Plazo que se le promete al cliente por el riel BANCARIO de Colombia (ACH).
 *
 * Es FIJO y no sale del backend a propósito. `/clp/cotizar` (y sus espejos VES
 * y USDT) devuelven `businessDays: 0` para el riel `CO`, que traducido literal
 * daba «llega el mismo día» — una promesa que la ACH colombiana no sostiene y
 * que además contradecía al propio botón del riel. Acreditación real observada:
 * de una hora a tres días, según cuándo abra el banco destino.
 *
 * Se redacta en HORAS y no en días hábiles porque el cliente cuenta desde que
 * aprieta el botón: «1 día hábil» un viernes a las 22:00 significa el lunes.
 */
export const ETA_CO_BANCARIO = '1 a 72 horas, según disponibilidad bancaria';

/** Plazo legible del par. Bre-B y Perú son instantáneos. */
export function etaDe(destino: DestinoRemesa, riel: string | null): string {
  if (esRielPropio(destino)) return 'minutos';
  if (destino === 'PE') return 'menos de 1 hora';
  if (destino === 'CO' && riel === 'COBREB') return 'menos de 1 hora';
  return ETA_CO_BANCARIO;
}

// ───────────────────────── Pago Móvil (destino Venezuela) ─────────────────────────

export interface ParametrosPagoMovil {
  /** `PAGO_MOVIL_GLOBAL`: habilita Pago Móvil para todos los bancos. */
  global: number;
  /** `PAGO_MOVIL_BANESCO`: habilita Pago Móvil sólo para Banesco. */
  banesco: number;
  /** `PAGO_MOVIL_MONTO_MAX`, en Bs. */
  montoMax: number;
}

/** `bancoID` de Banesco — el único habilitado cuando GLOBAL está apagado. */
export const BANCO_ID_BANESCO = 1;

/**
 * Filtro de la libreta de Venezuela según los flags de Pago Móvil.
 * Espejo exacto de `FiatCalculatorComponent.filtrarBeneficiariosPagoMovil` (:1166).
 *
 *  · BANESCO=1, GLOBAL=1 → todos
 *  · BANESCO=1, GLOBAL=0 → sólo `movilPay` de Banesco
 *  · BANESCO=0           → sólo los que NO son `movilPay`
 */
export function filtrarPorPagoMovil<T extends { movilPay?: number; bancoID?: number }>(
  beneficiarios: T[],
  p: ParametrosPagoMovil,
): T[] {
  if (p.banesco === 1 && p.global === 1) return beneficiarios;
  if (p.banesco === 1 && p.global === 0) {
    return beneficiarios.filter(
      (b) => b.movilPay === 0 || (b.movilPay === 1 && b.bancoID === BANCO_ID_BANESCO),
    );
  }
  return beneficiarios.filter((b) => b.movilPay === 0);
}

/**
 * El límite de Pago Móvil se compara contra los **Bs que RECIBE** el
 * beneficiario, nunca contra el monto enviado. Es el error más fácil de
 * cometer al portar esta regla.
 */
export function pagoMovilBloqueadoPorMonto(
  recibeVes: number | null | undefined,
  montoMax: number,
): boolean {
  return (recibeVes ?? 0) > montoMax;
}

/** Un beneficiario de Pago Móvil no se puede usar si el monto supera el techo. */
export function beneficiarioPagoMovilBloqueado(
  beneficiario: { movilPay?: number } | null | undefined,
  recibeVes: number | null | undefined,
  montoMax: number,
): boolean {
  return (
    beneficiario?.movilPay === 1 && pagoMovilBloqueadoPorMonto(recibeVes, montoMax)
  );
}

// ───────────────────────── Cambio de tasa ─────────────────────────

/**
 * Tolerancia relativa para decidir si el monto a recibir "cambió". Absorbe
 * ruido de redondeo del backend sin dejar pasar una variación real de tasa.
 */
export const TOLERANCIA_CAMBIO_TASA = 0.0001; // 0,01 %

/**
 * ¿Cambió lo que recibe el beneficiario respecto de lo que el usuario
 * confirmó? Se compara el `recibe`, no la tasa: es lo que el usuario vio y
 * lo único que le importa.
 */
export function cambioSignificativo(
  recibeConfirmado: number,
  recibeActual: number | null | undefined,
): boolean {
  if (recibeActual == null) return true;
  if (!(recibeConfirmado > 0)) return true;
  const delta = Math.abs(recibeActual - recibeConfirmado) / recibeConfirmado;
  return delta > TOLERANCIA_CAMBIO_TASA;
}

// ──────────────── Tasa honrada · SÓLO CLP→VES ────────────────
//
// En CLP→VES la regla al confirmar no es simétrica: se compara el monto en
// VES que vio el usuario contra el vigente y decide el SIGNO del movimiento.
//
//   · si honrar lo mostrado FAVORECE a Aguacate (o empata) → se honra, en
//     silencio, y el cliente recibe exactamente lo que vio;
//   · si va CONTRA Aguacate → cartel y reconfirmación explícita.
//
// Dos invariantes: el cliente nunca recibe menos de lo que vio sin haberlo
// aceptado, y Aguacate nunca absorbe una pérdida por un movimiento de tasa.
//
// Por qué acá y no en todos los rieles: en los de Vita el precio lo valida el
// proveedor (`expectedRecibeCop` / `expectedRecibePen` → `cambioValores`), así
// que honrar una tasa vieja sería rechazado. En CLP→VES el backend sólo exige
// que el trío `(tasa, tasaCambioId, recibe)` sea coherente, y `rates_calculated`
// es append-only: la fila vieja conserva su tasa. Ver la auditoría en la
// bitácora del cotizador.

/**
 * Epsilon en VES con el que el BACKEND decide si dos montos son el mismo:
 * `create.ts:124` y `transaction.service.ts:1312` comparan `origen × tasa`
 * contra `totalMonedaDestino` con esta tolerancia.
 *
 * NO es un umbral de negocio —no hay ninguno— sino el piso de ruido de la
 * aritmética. Se espeja el número del backend para no inventar un criterio
 * propio, y por eso se compara en VES absolutos: con umbral cero, dividir para
 * obtener un porcentaje mete error de punto flotante justo en el borde, que es
 * donde la regla decide.
 */
export const EPSILON_VES = 2.0;

/**
 * ¿Aplica la regla de tasa honrada? **Sólo CLP→VES.** Este es el único lugar
 * que acota el corredor: los otros nueve pares siguen con
 * `cambioSignificativo`, simétrico y con tolerancia de 0,01 %.
 */
export function aplicaHonrarTasa(
  origen: OrigenRemesa,
  destino: DestinoRemesa,
): boolean {
  return origen === 'CLP' && destino === 'VE';
}

/**
 * Lo que cuesta honrar el monto mostrado, en VES y CON SIGNO.
 * Positivo = la tasa bajó y honrar le cuesta plata a Aguacate.
 */
export function costoDeHonrarVes(
  recibeAntes: number,
  recibeAhora: number,
): number {
  return recibeAntes - recibeAhora;
}

/**
 * Lo mismo en relativo. **No decide nada** — es sólo para mostrar y reportar;
 * la decisión la toma `decisionTasa` con la diferencia absoluta.
 */
export function costoDeHonrarPct(
  recibeAntes: number,
  recibeAhora: number,
): number | null {
  if (!(recibeAntes > 0)) return null;
  return costoDeHonrarVes(recibeAntes, recibeAhora) / recibeAntes;
}

/**
 * ¿La cotización fresca cayó en la MISMA franja que la que vio el usuario?
 *
 * Honrar manda la fila de la franja vista, y la lambda valida el monto contra
 * los cortes VIGENTES de esa franja: si el operador los movió y el monto ya cae
 * en otra, descarta la orden sin avisar —y el front ya mostró «enviado»—. Con
 * el motor de márgenes los cortes se reinterpolan al agregar o quitar franjas,
 * así que el caso deja de ser teórico.
 *
 * Sin clave en ninguna de las dos (backend que no la publica) no hay con qué
 * comparar y rige la regla de siempre. Con clave en una sola no se puede
 * afirmar que sea la misma: se trata como distinta.
 */
export function mismaFranja(
  antes: string | null | undefined,
  ahora: string | null | undefined,
): boolean {
  const a = antes ?? null;
  const b = ahora ?? null;
  if (a === null && b === null) return true;
  return a === b;
}

/**
 * La regla. `'honrar'` = enviar con lo que el usuario vio, sin cartel.
 * `'reconfirmar'` = mostrar el cambio y esperar una confirmación nueva.
 *
 * Un cambio de franja reconfirma SIEMPRE, aunque el monto nuevo favorezca al
 * cliente: honrar mandaría la fila de una franja que ya no le corresponde al
 * monto (ver `mismaFranja`). Lo demás no cambia: el signo y `EPSILON_VES`.
 */
export function decisionTasa(
  recibeAntes: number,
  recibeAhora: number,
  franjaAntes?: string | null,
  franjaAhora?: string | null,
): 'honrar' | 'reconfirmar' {
  if (!mismaFranja(franjaAntes, franjaAhora)) return 'reconfirmar';
  return costoDeHonrarVes(recibeAntes, recibeAhora) > EPSILON_VES
    ? 'reconfirmar'
    : 'honrar';
}

/**
 * Espejo de la validación del backend, para correrla ANTES de despachar.
 *
 * Importa mucho más de lo que parece: si el trío no es coherente,
 * `POST transaccion` responde 200 igual (la validación de `create.ts` es la
 * misma, pero la de `transaction.service.ts:1301-1309` corre en el consumidor
 * de SQS) y `executeTransaction` hace `return` sin crear nada ni avisar. El
 * cliente vería «Transacción creada» y no existiría. Mejor fallar acá, fuerte
 * y visible, que mandar algo que se descarta en silencio.
 */
export function trioCoherente(
  monto: number,
  tasa: number,
  recibe: number,
): boolean {
  return Math.abs(monto * tasa - recibe) < EPSILON_VES;
}

// ───────────────────────── Motivos del backend ─────────────────────────

/** Traduce los códigos de `motivo` de Vita a lenguaje de cliente. */
export function motivoLegible(motivo: string | null | undefined): string | null {
  switch (motivo) {
    case 'INACTIVO':
      return 'Este destino no está disponible por ahora.';
    case 'SIN_TASA':
      return 'No hay tasa publicada en este momento.';
    case 'BAJO_MINIMO':
      return 'El monto está por debajo del mínimo.';
    case 'SIN_MARGEN':
      return 'No podemos cotizar este monto ahora.';
    default:
      return null;
  }
}

// ───────────────────────── Idempotencia ─────────────────────────

/**
 * Los rieles PROPIOS no tienen deduplicación en backend (a diferencia de los
 * de Vita, que sí: `uk_vtl_client_request_id` UNIQUE + replay).
 *
 * Consecuencia de diseño, deliberada: **nunca reintentar automáticamente un
 * envío por un riel propio**. Copiar el patrón de reintento de Vita a estos
 * rieles crearía duplicados que hoy no ocurren. Ante un fallo ambiguo se
 * verifica en el historial y se le informa al usuario — no se reenvía solo.
 */
export function permiteReintentoAutomatico(destino: DestinoRemesa): boolean {
  return !esRielPropio(destino);
}
